import React, { useRef, useState } from 'react';
import { FiUpload, FiDownload, FiCheck } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { FormAlert } from './form';
import CurrencyBadge from './CurrencyBadge';
import { formatMoney } from '../utils/money';

const ExpenseCsvImport = ({ modoOscuro, onImported, onCancel }) => {
  const { secureFetch } = useApi();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [parseErrors, setParseErrors] = useState([]);
  const [rows, setRows] = useState([]);
  const [csvRaw, setCsvRaw] = useState('');
  const [needsMapping, setNeedsMapping] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [fields, setFields] = useState([]);
  const [columnMap, setColumnMap] = useState({});

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const inputClass = modoOscuro ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300';

  const downloadTemplate = async () => {
    try {
      const res = await secureFetch('/api/import/egresos/template?format=xlsx');
      if (!res.ok) throw new Error('Plantilla no disponible');
      const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
      if (contentType.includes('text/csv')) {
        throw new Error('Reiniciá el backend para obtener la plantilla Excel.');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contsuite-plantilla-egresos.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('No se pudo descargar la plantilla.');
    }
  };

  const previewWithMap = async (csv, map) => {
    setError('');
    setLoading(true);
    try {
      const res = await secureFetch('/api/import/egresos/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv, columnMap: map }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al leer el archivo');
      if (data.needsMapping) {
        setNeedsMapping(true);
        setHeaders(data.headers || []);
        setFields(data.fields || []);
        setColumnMap(data.suggestedMap || {});
        setRows([]);
        setParseErrors(data.parseErrors || []);
        return;
      }
      setNeedsMapping(false);
      setRows(data.preview || []);
      setParseErrors(data.parseErrors || []);
      if (!data.preview?.length && data.parseErrors?.length) {
        setError('No se pudieron leer filas válidas del CSV.');
      }
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const csv = await file.text();
    setCsvRaw(csv);
    await previewWithMap(csv, null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const applyMapping = async () => {
    if (columnMap.concepto == null || columnMap.monto == null) {
      setError('Asigná al menos Concepto y Monto.');
      return;
    }
    await previewWithMap(csvRaw, columnMap);
  };

  const toggleRow = (line) => {
    setRows((prev) => prev.map((r) => (r.line === line ? { ...r, selected: !r.selected } : r)));
  };

  const handleImport = async () => {
    const selected = rows.filter((r) => r.selected !== false && !r.error);
    if (!selected.length) {
      setError('Seleccioná al menos una fila válida.');
      return;
    }
    setImporting(true);
    setError('');
    try {
      const res = await secureFetch('/api/import/egresos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: selected }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al importar');
      onImported?.(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = rows.filter((r) => r.selected !== false && !r.error).length;

  return (
    <div className={`rounded-2xl border ${box} p-4 sm:p-6 space-y-4`}>
      <div>
        <h2 className={`text-lg font-semibold ${textPri}`}>Importar egresos desde CSV</h2>
        <p className={`text-xs mt-1 ${textMuted}`}>
          Compatible con extractos bancarios o planilla propia. Si las columnas no coinciden, podés mapearlas manualmente.
        </p>
      </div>

      <FormAlert type="error">{error}</FormAlert>
      {parseErrors.length > 0 && !needsMapping && (
        <div className={`text-xs rounded-lg p-3 ${modoOscuro ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-900'}`}>
          {parseErrors.slice(0, 5).map((msg) => (
            <div key={msg}>{msg}</div>
          ))}
          {parseErrors.length > 5 && <div>… y {parseErrors.length - 5} más</div>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={downloadTemplate}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}
        >
          <FiDownload className="w-4 h-4" />
          Descargar plantilla
        </button>
        <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer bg-[#1F80FF] text-white hover:bg-[#004AAD] ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
          <FiUpload className="w-4 h-4" />
          {loading ? 'Procesando…' : 'Subir CSV'}
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
        </label>
        {onCancel && (
          <button type="button" onClick={onCancel} className={`px-3 py-2 rounded-xl text-sm border ${modoOscuro ? 'border-gray-600' : 'border-gray-200'}`}>
            Cancelar
          </button>
        )}
      </div>

      {needsMapping && headers.length > 0 && (
        <div className={`rounded-xl border p-4 space-y-3 ${modoOscuro ? 'border-amber-800 bg-amber-900/20' : 'border-amber-200 bg-amber-50'}`}>
          <p className={`text-sm font-semibold ${textPri}`}>Mapeá las columnas del archivo</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((field) => (
              <div key={field.id}>
                <label className={`block text-xs font-medium mb-1 ${textMuted}`}>
                  {field.label}{field.id === 'concepto' || field.id === 'monto' ? ' *' : ''}
                </label>
                <select
                  value={columnMap[field.id] ?? ''}
                  onChange={(e) => setColumnMap((p) => ({
                    ...p,
                    [field.id]: e.target.value === '' ? undefined : Number(e.target.value),
                  }))}
                  className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                >
                  <option value="">— No usar —</option>
                  {headers.map((h, idx) => (
                    <option key={`${h}-${idx}`} value={idx}>{h || `Columna ${idx + 1}`}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={applyMapping}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
          >
            Aplicar mapeo y previsualizar
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className={`overflow-x-auto rounded-xl border ${divide}`}>
            <table className="w-full text-xs">
              <thead className={modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th className="p-2 w-8" />
                  <th className="p-2 text-left">Fecha</th>
                  <th className="p-2 text-left">Concepto</th>
                  <th className="p-2 text-center">Mon.</th>
                  <th className="p-2 text-right">Monto</th>
                  <th className="p-2 text-right">≈ ARS</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${divide}`}>
                {rows.map((r) => (
                  <tr key={r.line} className={r.error ? 'opacity-60' : ''}>
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={r.selected !== false && !r.error}
                        disabled={!!r.error}
                        onChange={() => toggleRow(r.line)}
                      />
                    </td>
                    <td className="p-2 whitespace-nowrap">{r.fecha}</td>
                    <td className="p-2">
                      <div className={textPri}>{r.concepto}</div>
                      {r.error && <div className="text-red-500">{r.error}</div>}
                      {r.proveedor && <div className={textMuted}>{r.proveedor}</div>}
                      {r.cliente && <div className={textMuted}>Cliente: {r.cliente}</div>}
                    </td>
                    <td className="p-2 text-center">
                      <CurrencyBadge currency={r.moneda || 'ARS'} modoOscuro={modoOscuro} size="xs" />
                    </td>
                    <td className="p-2 text-right font-medium">{formatMoney(r.monto, r.moneda || 'ARS')}</td>
                    <td className="p-2 text-right">{r.montoArs != null ? formatMoney(r.montoArs, 'ARS') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={handleImport}
            disabled={importing || selectedCount === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1F80FF] text-white font-semibold text-sm disabled:opacity-50"
          >
            <FiCheck className="w-4 h-4" />
            {importing ? 'Importando…' : `Confirmar importación (${selectedCount})`}
          </button>
        </>
      )}
    </div>
  );
};

export default ExpenseCsvImport;
